import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useLocation } from '../../contexts/LocationContext';
import { useBusinessTypeContext } from '../../contexts/BusinessTypeContext';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import apiClient, { getErrorMessage } from '../../utils/api';

interface GoatExpenseLog {
  id: string;
  location_id: string;
  expense_date: string;
  amount: number;
  expense_type: string;
  shed_id?: string;
  description: string;
  receipt_number?: string;
  vendor?: string;
  created_at: string;
  created_by: string;
  shed_name?: string;
}

const GoatExpenseLogsPage: React.FC = () => {
  const { selectedLocation } = useLocation();
  const { businessType } = useBusinessTypeContext();
  const { user } = useAuth();
  const [logs, setLogs] = useState<GoatExpenseLog[]>([]);
  const [sheds, setSheds] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filters
  const getDefaultDates = () => {
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);
    return {
      startDate: sevenDaysAgo.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0]
    };
  };
  
  const defaultDates = getDefaultDates();
  const [startDate, setStartDate] = useState(defaultDates.startDate);
  const [endDate, setEndDate] = useState(defaultDates.endDate);
  const [selectedShed, setSelectedShed] = useState('');
  const [selectedExpenseType, setSelectedExpenseType] = useState('');

  useEffect(() => {
    if (selectedLocation?.id && businessType === 'goat') {
      loadSheds();
    }
  }, [selectedLocation?.id, businessType]);

  useEffect(() => {
    if (selectedLocation?.id && businessType === 'goat') {
      loadLogs();
    }
  }, [selectedLocation?.id, businessType, startDate, endDate, selectedShed, selectedExpenseType, sheds]);

  const loadSheds = async () => {
    try {
      const response = await apiClient.get(`/goat/sheds?location_id=${selectedLocation?.id}`);
      setSheds(response.data || []);
    } catch (err: any) {
      console.error('Failed to load sheds:', err);
    }
  };

  const loadLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ location_id: selectedLocation?.id || '' });
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (selectedShed) params.append('shed_id', selectedShed);
      if (selectedExpenseType) params.append('expense_type', selectedExpenseType);
      
      const response = await apiClient.get(`/goat/expense-logs?${params.toString()}`);
      const logsData = response.data || [];
      
      // Enrich with shed names
      const enrichedLogs = logsData.map((log: GoatExpenseLog) => ({
        ...log,
        shed_name: log.shed_id ? (sheds.find(s => s.id === log.shed_id)?.name || 'Unknown Shed') : 'N/A'
      }));
      
      setLogs(enrichedLogs);
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to load expense logs'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (expenseId: string) => {
    if (!window.confirm('Are you sure you want to delete this expense record?')) {
      return;
    }

    try {
      await apiClient.delete(`/goat/expense-logs/${expenseId}`);
      await loadLogs();
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to delete expense'));
    }
  };

  const clearFilters = () => {
    const defaultDates = getDefaultDates();
    setStartDate(defaultDates.startDate);
    setEndDate(defaultDates.endDate);
    setSelectedShed('');
    setSelectedExpenseType('');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getTotalAmount = () => {
    return logs.reduce((sum, log) => sum + log.amount, 0).toFixed(2);
  };

  const getExpenseTypeColor = (type: string) => {
    switch (type.toLowerCase()) {
      case 'feed': return 'bg-green-100 text-green-800';
      case 'medicine': return 'bg-red-100 text-red-800';
      case 'salary': return 'bg-blue-100 text-blue-800';
      case 'maintenance': return 'bg-yellow-100 text-yellow-800';
      case 'construction': return 'bg-orange-100 text-orange-800';
      case 'goat_purchase': return 'bg-indigo-100 text-indigo-800';
      case 'misc': return 'bg-purple-100 text-purple-800';
      case 'labor': return 'bg-blue-100 text-blue-800';
      case 'equipment': return 'bg-purple-100 text-purple-800';
      case 'other': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (businessType !== 'goat') {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">This page is only available for goat farming locations.</p>
        <Link to="/dashboard" className="text-blue-600 hover:text-blue-800">Go to Dashboard</Link>
      </div>
    );
  }

  if (!selectedLocation) {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">Please select a location first</p>
        <Link to="/locations" className="text-blue-600 hover:text-blue-800">Go to Location Selection</Link>
      </div>
    );
  }

  return (
    <div className="container-mobile py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to="/goat/logs" className="btn-touch p-2 text-gray-400 hover:text-gray-600">
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">💸 Expense Logs</h1>
            <p className="text-gray-600">{selectedLocation.name}</p>
          </div>
        </div>
        <Link
          to="/goat/data-entry/expense"
          className="btn-touch bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center space-x-2"
        >
          <PlusIcon className="h-5 w-5" />
          <span>Add Expense</span>
        </Link>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Filters */}
      <div className="card p-4">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-4">
          <div>
            <label className="form-label">From Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="form-input"
            />
          </div>
          <div>
            <label className="form-label">To Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="form-input"
            />
          </div>
          <div>
            <label className="form-label">Shed</label>
            <select
              value={selectedShed}
              onChange={(e) => setSelectedShed(e.target.value)}
              className="form-input"
            >
              <option value="">All Sheds</option>
              {sheds.map((shed) => (
                <option key={shed.id} value={shed.id}>
                  {shed.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label">Expense Type</label>
            <select
              value={selectedExpenseType}
              onChange={(e) => setSelectedExpenseType(e.target.value)}
              className="form-input"
            >
              <option value="">All Types</option>
              <option value="feed">Feed</option>
              <option value="medicine">Medicine</option>
              <option value="salary">Salary</option>
              <option value="maintenance">Maintenance</option>
              <option value="construction">Construction</option>
              <option value="goat_purchase">Goat purchase</option>
              <option value="misc">Miscellaneous</option>
            </select>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={clearFilters}
            className="flex-1 bg-gray-100 text-gray-700 py-2 px-3 rounded-lg text-sm font-medium hover:bg-gray-200"
          >
            Reset Filters
          </button>
          <button
            onClick={loadLogs}
            className="flex-1 bg-blue-600 text-white py-2 px-3 rounded-lg text-sm font-medium hover:bg-blue-700"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      {logs.length > 0 && (
        <div className="card p-4">
          <div className="bg-red-50 p-3 rounded-lg text-center">
            <div className="text-xs text-red-600 font-medium">Total Expenses</div>
            <div className="text-lg font-bold text-red-700">₹{getTotalAmount()}</div>
          </div>
        </div>
      )}

      {/* Logs List */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Expense Records ({logs.length})
        </h2>

        {loading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : logs.length > 0 ? (
          <div className="space-y-3">
            {logs.map((log) => (
              <div key={log.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="font-semibold text-gray-900">{log.shed_name}</span>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getExpenseTypeColor(log.expense_type)}`}>
                        {log.expense_type}
                      </span>
                      <span className="text-sm text-gray-500">{formatDate(log.expense_date)}</span>
                    </div>
                    <div className="text-sm text-gray-600 space-y-1">
                      <div className="text-lg font-bold text-red-600">₹{log.amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                      <div>{log.description}</div>
                      {log.vendor && <div>Vendor: {log.vendor}</div>}
                      {log.receipt_number && <div>Receipt: {log.receipt_number}</div>}
                    </div>
                  </div>
                  {user?.role === 'admin' && (
                    <button
                      onClick={() => handleDelete(log.id)}
                      className="ml-2 p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-4xl mb-4">💸</div>
            <p className="text-gray-500 mb-4">No expense records found</p>
            <Link
              to="/goat/data-entry/expense"
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Add First Expense Entry
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default GoatExpenseLogsPage;
